import { Component, OnInit, Signal, computed } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Terms } from '@scenario/enums/terms.enum';
import { dispatch, select } from '@store/injectables';
import { UnitRatesViewModel, selectUnitRatesViewModel } from '@store/scenario/unit-rates/unit-rates-slice';
import {
  UserPreferencesViewModel,
  saveUserPreferences, selectUserPreferencesViewModel,
} from '@store/scenario/user-preferences/user-preferences-slice';
import { UnitRate } from '@scenario/models/unit-rate.model';
import { saveSelectedUnitRates, selectSelectedUnitRates } from '@store/scenario/unit-rates/selected-unit-rates-slice';
import { toObservable } from '@angular/core/rxjs-interop';
import { distinctUntilChanged } from 'rxjs';
import { currencySaved, selectHistoricalDataMode } from '@store/scenario/app/app-slice';
import { HistoricalDataMode } from '@scenario/enums/historical-data-mode.enum';
import { Unit } from '@scenario/models/unit.model';
import { InsightService } from '@app/services/insight.service';
import { UserPreferences } from '@scenario/models/user-preferences.model';
import { FormFieldComponent, FormFieldLabelDirective, IconComponent, SegmentedControlComponent, SegmentedControlOptionComponent } from '@totalenergiescode/tds-angular';
import { InfoIconTooltipComponent } from '@app/tooltips/info-icon-tooltip/info-icon-tooltip.component';
import { MatTooltip } from '@angular/material/tooltip';
import { currenciesWhiteList } from '@configurations/scenario/currencies-white-list.configuration';

@Component({
    selector: 'app-scenario-unit-form',
    templateUrl: './scenario-unit-form.component.html',
    styleUrl: './scenario-unit-form.component.scss',
    imports: [FormsModule, ReactiveFormsModule, FormFieldComponent, FormFieldLabelDirective, InfoIconTooltipComponent, IconComponent, MatTooltip, SegmentedControlComponent, SegmentedControlOptionComponent]
})
export class ScenarioUnitFormComponent implements OnInit {
  dispatch = dispatch();
  userPreferencesSignal: Signal<UserPreferencesViewModel> = select(selectUserPreferencesViewModel);
  userPreferences$ = toObservable(this.userPreferencesSignal);
  unitRatesViewModelSignal: Signal<UnitRatesViewModel> = select(selectUnitRatesViewModel);
  unitRatesViewModelSignal$ = toObservable(this.unitRatesViewModelSignal);
  selectedUnitRatesSignal: Signal<UnitRate> = select(selectSelectedUnitRates);
  advancedMode = computed(()=> this.userPreferencesSignal().userPreferences.advanced);
  historicalDataModeSignal: Signal<HistoricalDataMode> = select(selectHistoricalDataMode);
  historicalDataMode$ = toObservable(this.historicalDataModeSignal);


  searchScenarioUnitForm = new FormGroup({
    currency: new FormControl(''),
    terms: new FormControl(''),
    year: new FormControl(0),
  });

  currencies = computed(() => {
    return this.getCurrencies(this.unitRatesViewModelSignal().unitRates);
  });
  terms = computed(() => {
    return this.getTerms(this.unitRatesViewModelSignal().unitRates);
  });

  years = computed(() => {
    return this.getYears(this.unitRatesViewModelSignal().unitRates);
  });

  unitRates: UnitRate[];

    constructor(private readonly insightService: InsightService) {

    }

    ngOnInit() {
    this.unitRatesViewModelSignal$.subscribe(unitRate => {
      this.unitRates = unitRate.unitRates;
      this.searchScenarioUnitForm.patchValue({
        currency: 'EUR',
        terms: 'REAL',
        year: 2024,
      });
      this.loadUserPreferences();
    });

    this.searchScenarioUnitForm.controls['terms'].valueChanges.subscribe(term => {
      const yearField = this.searchScenarioUnitForm.controls['year'];
      if (term === 'NOMINAL') {
        yearField.patchValue(null, { emitEvent: false });
        yearField.disable();
      } else {
        yearField.patchValue(this.years()[0], { emitEvent: false });
        yearField.enable();
      }
    });

    this.searchScenarioUnitForm.valueChanges.subscribe(data => {
      const selectedUnitRate = data.year
        ? this.unitRates.find(
            unitRate =>
              unitRate.to.currency === data.currency &&
              unitRate.to.term === data.terms &&
              unitRate.to.realYear === Number(data.year),
          )
        : this.unitRates.find(
            unitRate => unitRate.to.currency === data.currency && unitRate.to.term === data.terms,
          );
      this.compareUnit(selectedUnitRate?.to);
      if (selectedUnitRate) {
        const currency: string | null = this.searchScenarioUnitForm.controls['currency'].value;
        const terms: string | null = this.searchScenarioUnitForm.controls['terms'].value;
        const year: number | null = this.searchScenarioUnitForm.controls['year'].value;
        if (currency != null && terms != null) {
          this.insightService.addCurrencyAttributesAwsRum(currency, terms, year);
        }
        this.dispatch(saveSelectedUnitRates(selectedUnitRate));
      }
      });

    this.userPreferences$.subscribe(preferences => {
      if (this.unitRates && !preferences.isFetching) {
        this.searchScenarioUnitForm.patchValue({
          currency: preferences.userPreferences.unitCurrency,
          terms: preferences.userPreferences.unitTerm,
          year: preferences.userPreferences.unitRealYear,
        });
      }
    });

    this.historicalDataMode$.pipe(distinctUntilChanged()).subscribe(mode => {
      this.applyHistoricalUnitSelection(mode);
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  switchTerm(term: any) {
    switch (term) {
      case Terms.REAL:
        this.searchScenarioUnitForm.patchValue({ terms: 'REAL' });
        break;

      case Terms.NOMINAL:
        this.searchScenarioUnitForm.patchValue({ terms: 'NOMINAL' });
        break;

      default:
        this.searchScenarioUnitForm.patchValue({ terms: 'REAL' });
    }
  }

  loadUserPreferences() {
    if (this.userPreferencesSignal().userPreferences) {
      this.searchScenarioUnitForm.setValue({
        currency: this.userPreferencesSignal().userPreferences.unitCurrency,
        terms: this.userPreferencesSignal().userPreferences.unitTerm,
        year: this.userPreferencesSignal().userPreferences.unitRealYear,
      });
    }
  }

  getCurrencies(unitRates: UnitRate[]): string[] {
    return Array.from(new Set(unitRates
      .filter(unitRate => ['NOMINAL', 'REAL'].includes(unitRate.to.term))
      .map(unitRate => unitRate.to.currency)))
      .filter(currency => currenciesWhiteList.includes(currency));
  }

  getYears(unitRates: UnitRate[]): number[] {
    return Array.from(
      new Set(
        unitRates
          .filter(unitRate => unitRate.to.realYear != null)
          .map(unitRate => unitRate.to.realYear as number)
          .sort((a, b) => b - a),
      ),
    );
  }

  getTerms(unitRates: UnitRate[]): string[] {
    return Array.from(new Set(unitRates
      .filter(unitRate => ['NOMINAL', 'REAL'].includes(unitRate.to.term))
      .map(unitRate => unitRate.to.term)
      .sort((a, b) => b.localeCompare(a)), // real should be before nominal
      ),
    );
  }


  compareUnit(unit?: Unit) {
    if (
      this.userPreferencesSignal().userPreferences.unitCurrency === unit?.currency &&
      this.userPreferencesSignal().userPreferences.unitTerm === unit?.term &&
      this.userPreferencesSignal().userPreferences.unitRealYear === unit?.realYear
    ) {
      this.dispatch(currencySaved(true));
    } else {
      this.dispatch(currencySaved(false));
    }
  }

  loadTermsValue() {
    if (this.searchScenarioUnitForm.controls['terms'].value === 'REAL') {
      return Terms.REAL;
    }
    return Terms.NOMINAL;
  }

  saveUserPreferences() {
    const userPreferences: UserPreferences = {
      unitId: this.selectedUnitRatesSignal().to.id,
      unitCurrency: this.selectedUnitRatesSignal().to.currency,
      unitTerm: this.selectedUnitRatesSignal().to.term,
      unitRealYear: this.selectedUnitRatesSignal().to.realYear,
      advanced: this.userPreferencesSignal().userPreferences.advanced,
      display: this.userPreferencesSignal().userPreferences.display
    };
    this.dispatch(saveUserPreferences(userPreferences));
  }

  private applyHistoricalUnitSelection(mode: HistoricalDataMode) {
    if (mode !== HistoricalDataMode.HISTORICAL) {
      return;
    }
    this.searchScenarioUnitForm.patchValue({ terms: 'NOMINAL' });

    const userPreferences: UserPreferences = {
      unitId: this.selectedUnitRatesSignal().to.id,
      unitCurrency: this.selectedUnitRatesSignal().to.currency,
      unitTerm: 'NOMINAL',
      unitRealYear: null,
      advanced: this.userPreferencesSignal().userPreferences.advanced,
      display: this.userPreferencesSignal().userPreferences.display
    };
    this.dispatch(saveUserPreferences(userPreferences));
  }
}
